import React, { useState } from 'react';
import { 
  Trophy, 
  ShieldCheck, 
  LogOut, 
  AlertCircle, 
  Award, 
  RotateCcw 
} from 'lucide-react';
import './Quiz20.css';
import QuestionText from '../QuestionText';
import OptionText from '../OptionText';

// Option 2 point ladder ending at 20,000 points
export const LADDER_POINTS = [
  5, 10, 20, 40, 80,
  150, 300, 625, 1250, 2500,
  5000, 7500, 10000, 15000, 20000
];

// Milestones: Q5 (index 4 -> 80 pts), Q10 (index 9 -> 2,500 pts)
export const MILESTONE_INDEXES = [4, 9];

/**
 * 1. Options Grid Sub-Component
 */
function Quiz20Options({ options, selectedIndex, isRevealed, onSelectOption }) {
  const getButtonClass = (index, option) => {
    let classes = ['quiz20-option-btn'];
    if (selectedIndex === index) classes.push('selected');
    if (isRevealed) {
      if (option.isCorrect) classes.push('correct');
      else if (selectedIndex === index) classes.push('wrong');
    }
    return classes.join(' ');
  };

  return (
    <div className="quiz20-options-grid">
      {options.map((opt, idx) => (
        <button
          key={idx}
          type="button"
          disabled={isRevealed}
          className={getButtonClass(idx, opt)}
          onClick={() => onSelectOption(idx)}
        >
          <span className="quiz20-option-key">{String.fromCharCode(65 + idx)}</span>
          <span className="quiz20-option-text"><OptionText text={opt.text} /></span>
        </button>
      ))}
    </div>
  );
}

/**
 * 2. Main Question Stage Sub-Component
 */
function Quiz20Stage({ 
  currentIndex, 
  question, 
  selectedIndex, 
  isRevealed, 
  bankedPoints, 
  onSelectOption, 
  onWalkAway 
}) {
  const currentPrize = LADDER_POINTS[currentIndex];

  return (
    <div className="quiz20-stage">
      <div className="quiz20-stage-header">
        <span className="quiz20-q-counter">Question {currentIndex + 1} of 15</span>
        <span className="quiz20-target-badge">
          Playing for: <strong>{currentPrize.toLocaleString()} pts</strong>
        </span>
      </div>

      <div className="quiz20-question-card">
        <h2><QuestionText text={question.text} /></h2>
      </div>

      <Quiz20Options
        options={question.options || []}
        selectedIndex={selectedIndex}
        isRevealed={isRevealed}
        onSelectOption={onSelectOption}
      />

      <div className="quiz20-stage-footer">
        <button
          type="button"
          className="quiz20-walk-btn"
          disabled={isRevealed}
          onClick={onWalkAway}
        >
          <LogOut size={16} />
          Walk Away with {bankedPoints.toLocaleString()} pts
        </button>
      </div>
    </div>
  );
}

/**
 * 3. Side Ladder Sub-Component
 */
function Quiz20Ladder({ currentIndex }) {
  return (
    <aside className="quiz20-ladder-aside">
      <div className="quiz20-ladder-title">
        <Trophy size={16} />
        <span>Prize Ladder</span>
      </div>

      <div className="quiz20-ladder-list">
        {[...LADDER_POINTS].reverse().map((points, revIndex) => {
          const actualIndex = 14 - revIndex;
          const isCurrent = actualIndex === currentIndex;
          const isPassed = actualIndex < currentIndex;
          const isMilestone = MILESTONE_INDEXES.includes(actualIndex);

          let rowClass = 'quiz20-ladder-row';
          if (isCurrent) rowClass += ' active';
          else if (isPassed) rowClass += ' passed';
          if (isMilestone) rowClass += ' milestone';

          return (
            <div key={actualIndex} className={rowClass}>
              <span className="ladder-step">
                Q{actualIndex + 1}
                {isMilestone && <ShieldCheck size={13} className="milestone-shield" />}
              </span>
              <span className="ladder-points">{points.toLocaleString()} pts</span>
            </div>
          );
        })}
      </div>
    </aside>
  );
}

/**
 * 4. Game Result Card Sub-Component
 */
function Quiz20Result({ result, score, onRestart }) {
  const isWin = result === 'win';
  const isWalked = result === 'walked';

  return (
    <div className="quiz20-result-card">
      <div className={`quiz20-result-icon ${isWin ? 'win' : isWalked ? 'walked' : 'wrong'}`}>
        {isWin ? <Award size={52} /> : isWalked ? <LogOut size={48} /> : <AlertCircle size={48} />}
      </div>

      <h2>
        {isWin && 'Grand Champion!'}
        {isWalked && 'Safe Walk Away!'}
        {!isWin && !isWalked && 'End of Challenge!'}
      </h2>

      <p className="quiz20-result-score">
        You won <strong>{score.toLocaleString()}</strong> Points
      </p>

      <p className="quiz20-result-desc">
        {isWin && 'Outstanding! You answered all 15 questions correctly and conquered the 20K Challenge.'}
        {isWalked && 'Smart decision! You preserved your banked points instead of taking the risk.'}
        {!isWin && !isWalked && 'A tricky question caught you, but your protected safety milestone holds your score.'}
      </p>

      <button type="button" className="quiz20-restart-btn" onClick={onRestart}>
        <RotateCcw size={16} /> Try Again
      </button>
    </div>
  );
}

/**
 * Main Orchestrator Component: Quiz20
 */
export default function Quiz20({ questions = [], onFinish }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [isRevealed, setIsRevealed] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [gameResult, setGameResult] = useState(null); // 'win' | 'walked' | 'wrong'
  const [finalScore, setFinalScore] = useState(0);

  const bankedPoints = currentIndex > 0 ? LADDER_POINTS[currentIndex - 1] : 0;

  const calculateFloorScore = () => {
    if (currentIndex > 9) return LADDER_POINTS[9]; // Q10 locked: 2,500 pts
    if (currentIndex > 4) return LADDER_POINTS[4]; // Q5 locked: 80 pts
    return 0;
  };

  const handleWalkAway = () => {
    setGameOver(true);
    setGameResult('walked');
    setFinalScore(bankedPoints);

    if (onFinish) {
      onFinish({
        score: bankedPoints,
        outcome: 'walked',
        reachedQuestion: currentIndex + 1
      });
    }
  };

  const handleSelectOption = (index) => {
    if (isRevealed || gameOver) return;

    setSelectedIndex(index);
    setIsRevealed(true);

    const isCorrect = questions[currentIndex]?.options?.[index]?.isCorrect;

    setTimeout(() => {
      if (isCorrect) {
        if (currentIndex === 14) {
          // Solved final question (20,000 pts)
          const winningScore = LADDER_POINTS[14];
          setGameOver(true);
          setGameResult('win');
          setFinalScore(winningScore);

          if (onFinish) {
            onFinish({
              score: winningScore,
              outcome: 'win',
              reachedQuestion: 15
            });
          }
        } else {
          setCurrentIndex((prev) => prev + 1);
          setSelectedIndex(null);
          setIsRevealed(false);
        }
      } else {
        const floor = calculateFloorScore();
        setGameOver(true);
        setGameResult('wrong');
        setFinalScore(floor);

        if (onFinish) {
          onFinish({
            score: floor,
            outcome: 'wrong',
            reachedQuestion: currentIndex + 1
          });
        }
      }
    }, 1400);
  };

  const handleRestart = () => {
    setCurrentIndex(0);
    setSelectedIndex(null);
    setIsRevealed(false);
    setGameOver(false);
    setGameResult(null);
    setFinalScore(0);
  };

  if (gameOver) {
    return <Quiz20Result result={gameResult} score={finalScore} onRestart={handleRestart} />;
  }

  const currentQuestion = questions[currentIndex];
  if (!currentQuestion) return null;

  return (
    <div className="quiz20-container">
      <Quiz20Ladder currentIndex={currentIndex} />
      <Quiz20Stage
        currentIndex={currentIndex}
        question={currentQuestion}
        selectedIndex={selectedIndex}
        isRevealed={isRevealed}
        bankedPoints={bankedPoints}
        onSelectOption={handleSelectOption}
        onWalkAway={handleWalkAway}
      />
    </div>
  );
}